import {useEffect, useEffectEvent} from 'react'

import {IssueIndexPage, IssueViewsPage, RepositoryMilestoneIndexPage} from '../lazy-routes'
import {preloadWhenIdle} from '../utils/preload-when-idle'

/**
 * Preloads direct sidebar destinations during browser idle time.
 *
 * Includes all pages linked from the sidebar nav: Issues, Milestones
 * index, and Views. Labels is handled by the Data Router `issues` app and
 * does not need preloading here. Pages reachable via deeper navigation
 * (e.g. milestone show, milestone edit) are preloaded from the pages that
 * link to them.
 *
 * Call this from the RepoIssuesSidebar wrapper so that every page rendering
 * the sidebar automatically preloads its siblings. `.preload()` is
 * idempotent, so overlap with per-page calls is harmless.
 *
 * When `issue_pinned_views` is fully rolled out and the sidebar renders
 * unconditionally, per-page preloads that duplicate this hook's set can be
 * removed. Per-page preloads for non-sidebar destinations (e.g.
 * RepositoryMilestoneEditPage from RepositoryMilestonePage) must be kept.
 */
export function usePreloadSidebarPages(): void {
  const schedulePreloads = useEffectEvent(() => {
    return preloadWhenIdle(() => {
      IssueIndexPage.preload()
      RepositoryMilestoneIndexPage.preload()
      IssueViewsPage.preload()
    })
  })
  useEffect(() => {
    return schedulePreloads()
  }, [])
}
